import React, { ReactNode } from 'react';
import { Box, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SxProps, Theme } from '@mui/material/styles';

interface ResponsiveContainerProps {
    children: ReactNode;
    className?: string;
    fullWidth?: boolean;
    minHeight?: string;
    mobileMinHeight?: string;
    padding?: string;
    mobilePadding?: string;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
    bgcolor?: string;
    sx?: SxProps<Theme>;  // Added SX prop support
}

const StyledContainer = styled(Container, {
    shouldForwardProp: (prop) =>
        prop !== 'mobileMinHeight' &&
        prop !== 'mobilePadding'
})<{
    mobileMinHeight?: string;
    mobilePadding?: string;
}>(({ theme, mobileMinHeight, mobilePadding }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '24px',
    minHeight: '200px',

    [theme.breakpoints.down('md')]: {
        padding: mobilePadding || '16px',
        minHeight: mobileMinHeight || '150px',
        maxWidth: '100%', // Prevent container from exceeding viewport
    }
}));

const ResponsiveContainer: React.FC<ResponsiveContainerProps> = ({
    children,
    className = '',
    fullWidth = false,
    minHeight,
    mobileMinHeight,
    padding,
    mobilePadding,
    maxWidth = 'lg',
    bgcolor,
    sx
}) => {
    return (
        <Box
            sx={{
                width: '100%',
                bgcolor: 'transparent',  // Always transparent to show parent background
                ...sx  // Spread the sx prop
            }}
        >
            <StyledContainer
                maxWidth={fullWidth ? false : maxWidth}
                className={className}
                sx={{
                    minHeight: minHeight || undefined,
                    padding: padding || undefined
                }}
                mobileMinHeight={mobileMinHeight}
                mobilePadding={mobilePadding}
            >
                {children}
            </StyledContainer>
        </Box>
    );
};

export default ResponsiveContainer;

/* Usage Example:
<ResponsiveContainer
    maxWidth="lg"
    minHeight="400px"
    mobileMinHeight="300px"
    padding="32px"
    mobilePadding="16px"
    bgcolor="#f5f5f5"
>
    <Content />
</ResponsiveContainer>
*/